import { Button, Filters, Page } from "@rentiohq/web-shared/dist/components";
import React from "react";
import { AcademyHighlight } from "./components/AcademyHighlight";
import { AcademyScore } from "./components/AcademyScore";
import {
  ACADEMY_BASE_PATH,
  ACADEMY_CHAPTERS,
  ACADEMY_SHORT_PATH,
  ACADEMY_START_STEPS,
} from "./RentmatchAcademy.constants";
import {
  useAcademyQuizAnswers,
  useAcademySearchQuery,
} from "./RentmatchAcademy.hooks";
import { ACADEMY_MEDIA } from "./RentmatchAcademy.media";
import { isSearchQuery, searchAcademy } from "./RentmatchAcademy.search";
import * as S from "./RentmatchAcademy.styles";
import { EAcademyMedia } from "./RentmatchAcademy.types";
import {
  getAcademyScore,
  getAcademyText,
  getChapterKey,
  getChapterNumber,
  getChapterScore,
  getStartStepKey,
  getUiKey,
} from "./RentmatchAcademy.utils";

// A video's card shows its still, an image itself.
const getCoverSrc = (mediaId: EAcademyMedia) => {
  const media = ACADEMY_MEDIA[mediaId];

  return media.poster ?? media.src;
};

export const RentmatchAcademy = () => {
  const { answers } = useAcademyQuizAnswers();
  const totalScore = getAcademyScore(answers);
  const { query, setQuery } = useAcademySearchQuery();
  const isSearching = isSearchQuery(query);
  const results = React.useMemo(() => searchAcademy(query), [query]);

  // Render
  const renderStart = () => (
    <S.Start>
      <S.SectionTitle>{getAcademyText(getUiKey("start.title"))}</S.SectionTitle>
      <S.StartSteps>
        {ACADEMY_START_STEPS.map((step, index) => (
          <S.StartStep key={step.id}>
            <S.StartNumber>{index + 1}</S.StartNumber>
            <div>
              <S.CardTitle>
                {getAcademyText(getStartStepKey(step.id, "title"))}
              </S.CardTitle>
              <S.CardText>
                {getAcademyText(getStartStepKey(step.id, "body"))}
              </S.CardText>
              <S.StartLinks>
                <S.StartLink to={`${ACADEMY_BASE_PATH}/${step.chapter}`}>
                  {getAcademyText(getUiKey("start.step.more"))}
                </S.StartLink>
                {step.links?.map(link => (
                  <S.StartLink key={link.id} to={link.path}>
                    {getAcademyText(
                      getStartStepKey(step.id, `link.${link.id}`),
                    )}
                  </S.StartLink>
                ))}
              </S.StartLinks>
            </div>
          </S.StartStep>
        ))}
      </S.StartSteps>
      <S.CardText>{getAcademyText(getUiKey("start.note"))}</S.CardText>
    </S.Start>
  );

  const renderShortVersion = () => (
    <S.ShortVersion>
      <div>
        <S.CardTitle>{getAcademyText(getUiKey("short.title"))}</S.CardTitle>
        <S.CardText>{getAcademyText(getUiKey("short.description"))}</S.CardText>
      </div>
      <Button
        appearance="primary"
        url={`${ACADEMY_BASE_PATH}/${ACADEMY_SHORT_PATH}`}
      >
        {getAcademyText(getUiKey("short.cta"))}
      </Button>
    </S.ShortVersion>
  );

  const renderChapters = () => (
    <>
      <S.SectionTitle>{getAcademyText(getUiKey("full.title"))}</S.SectionTitle>
      <S.CardText>
        {getAcademyText(getUiKey("full.description"), {
          count: `${ACADEMY_CHAPTERS.length}`,
        })}
      </S.CardText>
      <S.ChapterGrid>
        {ACADEMY_CHAPTERS.map(chapter => (
          <S.ChapterCard
            key={chapter.id}
            to={`${ACADEMY_BASE_PATH}/${chapter.id}`}
          >
            <S.ChapterCoverWrap>
              <S.ChapterCover
                src={getCoverSrc(chapter.cover)}
                alt=""
                loading="lazy"
              />
              <S.ChapterScore>
                <AcademyScore score={getChapterScore(chapter, answers)} />
              </S.ChapterScore>
            </S.ChapterCoverWrap>
            <S.ChapterBody>
              <S.ChapterLabel>
                {getAcademyText(getUiKey("chapter.label"), {
                  number: `${getChapterNumber(chapter)}`,
                })}
              </S.ChapterLabel>
              <S.CardTitle>
                {getAcademyText(getChapterKey(chapter.id, "title"))}
              </S.CardTitle>
              <S.CardText>
                {getAcademyText(getChapterKey(chapter.id, "intro"))}
              </S.CardText>
            </S.ChapterBody>
          </S.ChapterCard>
        ))}
      </S.ChapterGrid>
    </>
  );

  const renderSearch = () => (
    <S.Search>
      <Filters
        queryValue={query}
        queryPlaceholder={getAcademyText(getUiKey("search.placeholder"))}
        onQueryChange={setQuery}
        onQueryClear={() => setQuery("")}
      />
    </S.Search>
  );

  const renderResults = () => {
    if (results.length === 0) {
      return (
        <S.CardText>
          {getAcademyText(getUiKey("search.empty"), { query: query.trim() })}
        </S.CardText>
      );
    }

    return (
      <>
        <S.SectionTitle>
          {getAcademyText(getUiKey("search.results"), {
            query: query.trim(),
            count: `${results.length}`,
          })}
        </S.SectionTitle>
        <S.Results>
          {results.map(result => (
            <S.Result key={result.id} to={result.path}>
              <S.ChapterLabel>{result.context}</S.ChapterLabel>
              <S.CardTitle>
                <AcademyHighlight text={result.title} query={query} />
              </S.CardTitle>
              <S.CardText>
                <AcademyHighlight text={result.snippet} query={query} />
              </S.CardText>
            </S.Result>
          ))}
        </S.Results>
      </>
    );
  };

  return (
    <Page
      title={getAcademyText(getUiKey("title"))}
      subtitle={getAcademyText(getUiKey("subtitle"))}
      secondaryActionNode={<AcademyScore score={totalScore} />}
    >
      {renderSearch()}
      {isSearching ? (
        renderResults()
      ) : (
        <>
          {renderStart()}
          {renderShortVersion()}
          {renderChapters()}
        </>
      )}
    </Page>
  );
};
